import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface ServicesNavbarProps {
  scrolled: boolean;
}

export default function ServicesNavbar({ scrolled }: ServicesNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-sm py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="w-full px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo with FMR tooltip */}
          <Link to="/" className="flex items-center gap-4 cursor-pointer group relative">
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
                alt="House of FMR"
                className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-amber-400/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div className="hidden sm:block">
              <p className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300 font-display text-xl tracking-wide">House of FMR</p>
              <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-medium">Flair • Mixology • Rentals</p>
            </div>
            {/* FMR Tooltip */}
            <div className="absolute top-full left-0 mt-2 px-3 py-1.5 bg-stone-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Flair • Mixology • Rentals
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/')
                  ? 'text-amber-600 font-semibold'
                  : scrolled
                    ? 'text-stone-600 hover:text-amber-600'
                    : 'text-white/80 hover:text-amber-300'
              }`}
            >
              Home
            </Link>
            <button
              onClick={() => scrollToSection('pricing')}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-amber-600 font-semibold'
                  : 'text-amber-300 font-semibold'
              }`}
            >
              Services & Pricing
            </button>
            <Link
              to="/equipment-hire"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/equipment-hire')
                  ? 'text-amber-600 font-semibold'
                  : scrolled
                    ? 'text-stone-600 hover:text-amber-600'
                    : 'text-white/80 hover:text-amber-300'
              }`}
            >
              Equipment Hire
            </Link>
            <Link
              to="/gallery"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/gallery')
                  ? 'text-amber-600 font-semibold'
                  : scrolled
                    ? 'text-stone-600 hover:text-amber-600'
                    : 'text-white/80 hover:text-amber-300'
              }`}
            >
              Gallery
            </Link>
            <Link
              to="/shop"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/shop')
                  ? 'text-amber-600 font-semibold'
                  : scrolled
                    ? 'text-stone-600 hover:text-amber-600'
                    : 'text-white/80 hover:text-amber-300'
              }`}
            >
              Shop
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:0411164144"
              className={`flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-stone-600 hover:text-amber-600'
                  : 'text-white/80 hover:text-amber-300'
              }`}
            >
              <i className="ri-phone-line"></i>
              0411 164 144
            </a>
            <button
              onClick={() => scrollToSection('consultation')}
              className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-400 hover:to-yellow-300 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/25"
            >
              Free Consultation
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${
              scrolled ? 'bg-stone-100 text-stone-900' : 'bg-white/10 text-white'
            }`}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 bg-white rounded-2xl p-5 shadow-xl">
            <div className="space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-line text-base"></i>Home
              </Link>
              <button
                onClick={() => scrollToSection('pricing')}
                className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-amber-600 bg-amber-50 rounded-lg cursor-pointer"
              >
                <i className="ri-service-line text-base"></i>Services & Pricing
              </button>
              <Link
                to="/equipment-hire"
                className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-calendar-check-line text-base"></i>Equipment Hire
              </Link>
              <Link
                to="/gallery"
                className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-image-line text-base"></i>Gallery
              </Link>
              <Link
                to="/shop"
                className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-store-2-line text-base"></i>Shop
              </Link>
              <div className="h-px bg-stone-200 my-2"></div>
              <button
                onClick={() => scrollToSection('consultation')}
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-400 hover:to-yellow-300 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/25"
              >
                <i className="ri-calendar-line text-base"></i>Free Consultation
              </button>
              <a
                href="tel:0411164144"
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-stone-100 text-stone-700 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-base"></i>Call 0411 164 144
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}